import { defineStore } from 'pinia'
import { ref } from 'vue'

import {
  ApplicantRow,
  ApplicantStatus,
  DEFAULT_PAGE,
  DEFAULT_PAGE_SIZE,
  DEFAULT_SORT_BY,
  getInstagramInstantwinApplicants,
  GetInstagramInstantwinApplicantsParams,
  InstagramInstantwinApplicantsApiError,
  InstagramInstantwinApplicantsData,
  SortBy
} from '../api/getInstagramInstantwinApplicants'

type ErrorCode = 'PERMISSION_DENIED' | 'NOT_EXISTS' | 'UNKNOWN' | null
type ApplicantRange = { min?: number; max?: number }
type ApplicantEntryRange = { start?: string; end?: string }
export type ApplicantSearchFilters = {
  keyword?: string
  status?: ApplicantStatus[]
  entry?: ApplicantEntryRange
  applicantCount?: ApplicantRange
  totalEntryCount?: ApplicantRange
  totalWinningCount?: ApplicantRange
  detailsOpen: boolean
}

type ApplicantFilterParams = Partial<{
  keyword: string
  status: ApplicantStatus[]
  entryStartAt: string
  entryEndAt: string
  applicantCountMin: number
  applicantCountMax: number
  totalCampaignEntryCountMin: number
  totalCampaignEntryCountMax: number
  totalCampaignWinningCountMin: number
  totalCampaignWinningCountMax: number
}>

const normalizeRange = (range?: ApplicantRange): ApplicantRange | undefined => {
  if (!range) return undefined
  const normalized: ApplicantRange = {}
  if (typeof range.min !== 'undefined') {
    normalized.min = range.min
  }
  if (typeof range.max !== 'undefined') {
    normalized.max = range.max
  }
  if (typeof normalized.min === 'undefined' && typeof normalized.max === 'undefined') {
    return undefined
  }
  return normalized
}

const normalizeEntryRange = (entry?: ApplicantEntryRange): ApplicantEntryRange | undefined => {
  if (!entry) return undefined
  const normalized: ApplicantEntryRange = {}
  if (entry.start && entry.start.trim().length > 0) {
    normalized.start = entry.start
  }
  if (entry.end && entry.end.trim().length > 0) {
    normalized.end = entry.end
  }
  if (!normalized.start && !normalized.end) {
    return undefined
  }
  return normalized
}

const toSortByString = (payload: InstagramInstantwinApplicantsData, currentSort: SortBy): SortBy => {
  return currentSort ?? DEFAULT_SORT_BY
}

const resolveTotal = (payload: InstagramInstantwinApplicantsData): number => {
  if (typeof payload.total === 'number') {
    return payload.total
  }
  return payload.applicants?.length ?? 0
}

export const useInstagramInstantwinApplicantStore = defineStore('InstagramInstantwinApplicantStore', () => {
  const campaignId = ref<number | null>(null)
  const prizeId = ref<number | null>(null)
  const rows = ref<ApplicantRow[]>([])
  const total = ref(0)
  const sortBy = ref<SortBy>(DEFAULT_SORT_BY)
  const page = ref(DEFAULT_PAGE)
  const pageSize = ref(DEFAULT_PAGE_SIZE)
  const filters = ref<ApplicantSearchFilters>({ detailsOpen: false })
  const loading = ref(false)
  const loadingCsv = ref(false)
  const errorCode = ref<ErrorCode>(null)
  const errorMessage = ref<string | null>(null)

  const setError = (code: ErrorCode, message?: string) => {
    errorCode.value = code
    errorMessage.value = message ?? null
  }

  const applyPayload = (payload: InstagramInstantwinApplicantsData) => {
    rows.value = payload.applicants ?? []
    total.value = resolveTotal(payload)
    sortBy.value = toSortByString(payload, sortBy.value)
  }

  const ensureCampaignId = (id?: number): number | null => {
    if (typeof id === 'number' && id > 0) {
      campaignId.value = id
    }
    return campaignId.value
  }

  const ensurePrizeId = (id?: number): number | null => {
    if (typeof id === 'number' && id > 0) {
      prizeId.value = id
    }
    return prizeId.value
  }

  const buildFilterParams = (state: ApplicantSearchFilters): ApplicantFilterParams => {
    const params: ApplicantFilterParams = {}
    if (state.keyword) {
      params.keyword = state.keyword
    }
    if (state.status && state.status.length > 0) {
      params.status = state.status
    }

    const entryRange = state.detailsOpen ? state.entry : undefined
    if (entryRange?.start) {
      params.entryStartAt = entryRange.start
    }
    if (entryRange?.end) {
      params.entryEndAt = entryRange.end
    }

    if (state.detailsOpen) {
      const applicantRange = normalizeRange(state.applicantCount)
      if (typeof applicantRange?.min !== 'undefined') {
        params.applicantCountMin = applicantRange.min
      }
      if (typeof applicantRange?.max !== 'undefined') {
        params.applicantCountMax = applicantRange.max
      }

      const totalEntryRange = normalizeRange(state.totalEntryCount)
      if (typeof totalEntryRange?.min !== 'undefined') {
        params.totalCampaignEntryCountMin = totalEntryRange.min
      }
      if (typeof totalEntryRange?.max !== 'undefined') {
        params.totalCampaignEntryCountMax = totalEntryRange.max
      }

      const totalWinningRange = normalizeRange(state.totalWinningCount)
      if (typeof totalWinningRange?.min !== 'undefined') {
        params.totalCampaignWinningCountMin = totalWinningRange.min
      }
      if (typeof totalWinningRange?.max !== 'undefined') {
        params.totalCampaignWinningCountMax = totalWinningRange.max
      }
    }

    return params
  }

  const applyFilters = async (payload: ApplicantSearchFilters) => {
    const normalized: ApplicantSearchFilters = {
      keyword: payload.keyword?.trim() || undefined,
      status: payload.status && payload.status.length > 0 ? [...payload.status] : undefined,
      entry: payload.detailsOpen ? normalizeEntryRange(payload.entry) : undefined,
      applicantCount: payload.detailsOpen ? normalizeRange(payload.applicantCount) : undefined,
      totalEntryCount: payload.detailsOpen ? normalizeRange(payload.totalEntryCount) : undefined,
      totalWinningCount: payload.detailsOpen ? normalizeRange(payload.totalWinningCount) : undefined,
      detailsOpen: Boolean(payload.detailsOpen)
    }
    filters.value = normalized
    page.value = DEFAULT_PAGE
  }

  const fetch = async ({ campaignId: id, prizeId: pId }: { campaignId: number; prizeId?: number }) => {
    const resolvedId = ensureCampaignId(id)
    ensurePrizeId(pId)
    if (!resolvedId) {
      setError('UNKNOWN', 'campaignId is required')
      return
    }
    loading.value = true
    setError(null)
    try {
      const response = await getInstagramInstantwinApplicants({
        campaignId: resolvedId,
        prizeId: prizeId.value ?? undefined,
        sortBy: sortBy.value,
        page: page.value,
        pageSize: pageSize.value,
        forCsv: false,
        ...buildFilterParams(filters.value)
      })
      applyPayload(response.data)
    } catch (error) {
      handleError(error)
    } finally {
      loading.value = false
    }
  }

  const changeSort = async (newSort: SortBy) => {
    sortBy.value = newSort
    page.value = DEFAULT_PAGE
    if (!campaignId.value) {
      setError('UNKNOWN', 'campaignId is required')
      return
    }
    await fetch({ campaignId: campaignId.value, prizeId: prizeId.value ?? undefined })
  }

  const changePage = async (newPage: number) => {
    if (newPage < DEFAULT_PAGE || !campaignId.value) {
      return
    }
    page.value = newPage
    await fetch({ campaignId: campaignId.value, prizeId: prizeId.value ?? undefined })
  }

  const changePageSize = async (newSize: number) => {
    if (!campaignId.value) {
      setError('UNKNOWN', 'campaignId is required')
      return
    }
    pageSize.value = Math.max(1, Math.min(newSize, 100))
    page.value = DEFAULT_PAGE
    await fetch({ campaignId: campaignId.value, prizeId: prizeId.value ?? undefined })
  }

  const exportCsv = async (params: { campaignId: number; prizeId?: number }) => {
    const resolvedId = ensureCampaignId(params.campaignId)
    ensurePrizeId(params.prizeId)
    if (!resolvedId) {
      setError('UNKNOWN', 'campaignId is required')
      return { applicants: [] }
    }
    loadingCsv.value = true
    setError(null)
    try {
      const response = await getInstagramInstantwinApplicants({
        campaignId: resolvedId,
        prizeId: prizeId.value ?? undefined,
        sortBy: sortBy.value,
        forCsv: true,
        ...buildFilterParams(filters.value)
      } as GetInstagramInstantwinApplicantsParams)
      return response.data
    } catch (error) {
      handleError(error)
      return { applicants: [] }
    } finally {
      loadingCsv.value = false
    }
  }

  const handleError = (error: unknown) => {
    if (error instanceof InstagramInstantwinApplicantsApiError) {
      if (error.type === 'PERMISSION_DENIED' || error.type === 'NOT_EXISTS') {
        setError(error.type as ErrorCode, error.message)
        return
      }
      setError('UNKNOWN', error.message || error.type)
      return
    }
    setError('UNKNOWN')
  }

  const reset = () => {
    rows.value = []
    total.value = 0
    sortBy.value = DEFAULT_SORT_BY
    page.value = DEFAULT_PAGE
    pageSize.value = DEFAULT_PAGE_SIZE
    filters.value = { detailsOpen: false }
    loading.value = false
    loadingCsv.value = false
    campaignId.value = null
    prizeId.value = null
    setError(null)
  }

  return {
    campaignId,
    prizeId,
    rows,
    total,
    sortBy,
    page,
    pageSize,
    filters,
    loading,
    loadingCsv,
    errorCode,
    errorMessage,
    applyFilters,
    fetch,
    changeSort,
    changePage,
    changePageSize,
    exportCsv,
    reset
  }
})
